import _ from 'lodash';
import * as Settlements from '@/types/models/settlement';
import { accountHasOfficePermission } from '@/store/permissions';
import moment from 'moment';
import { mapGetters } from 'vuex';

export default {
  methods: {
    ...mapGetters('profiles', {
      findCompanyAccount: 'findCompanyAccount',
    }),
    // does the provided company pay for quickbooks?
    isQuickbooksEnabled(company) {
      return true || company;
    },
    quickbooksConnectFor(company) {
      return _.get(company, 'plugins.invoicing.quickbooks')
    },
    // has the provided company ever provided QB credentials?
    isQuickbooksConnected(company) {
      return !!this.quickbooksConnectFor(company);
    },
    // has the provided company provided current QB credentials?
    isQuickbooksCredentialsValid(company) {
      if (this.isQuickbooksConnected(company)) {
        const expiry = this.quickbooksCompanyCredentialExpiresMoment(company);
        return expiry ? moment().isBefore(expiry) : false;
      }
      return false;
    },
    daysUntilQuickbooksCredentialsExpire(company) {
      const now = moment();
      const expiry = this.quickbooksCompanyCredentialExpiresMoment(company);

      return expiry ? expiry.diff(now, 'day') : 0;
    },
    shouldUseQuickbooks(company) {
      return this.isQuickbooksEnabled(company) && this.isQuickbooksCredentialsValid(company);
    },
    currentUserHasCreateSettlementInvoicePermission(settlement) {
      const invoice = Settlements.invoice(settlement);
      const paidToCompanyAccount = this.findCompanyAccount()(invoice.paidTo.id);

      return this.isLtnAdmin || accountHasOfficePermission(paidToCompanyAccount);
    },
    currentUserHasCreateTransactionInvoicePermission(txn) {
      const customerCompanyAccount = this.findCompanyAccount()(txn.vendor.companyId);

      return this.isLtnAdmin || accountHasOfficePermission(customerCompanyAccount);
    },
    quickbooksPaymentTerms(companyId) {
      return this.qbSalesTerms[companyId];
    },
    quickbooksAccounts(companyId) {
      return this.qbAccounts[companyId];
    },
    customerCompanyName(customer) {
      return customer.qbCompanyName;
    },
    customerContactName(customer) {
      return customer.qbName;
    },
    customerDisplayName(customer) {
      return customer.qbDisplayName || customer.qbCompanyName;
    },
    /**
     * A vendor looks like this:
     {
       qbCompanyId: "1234",
       qbDisplayName: "*3 Brand Cattle Co.",
       qbName: "3 Brands",
       selectDisplay: "3 Brands"             <=== this is added by this function as a matter of consistency
     }
     */
    quickbooksVendors(companyId) {
      const vendors = this.qbVendors[companyId] || [];
      // eslint-disable-next-line no-return-assign,no-param-reassign
      return vendors.map((vendor) => {
        // eslint-disable-next-line no-param-reassign
        vendor.selectDisplay = this.customerDisplayName(vendor);
        return vendor;
      });
    },
    quickbooksCustomers(companyId) {
      const customers = this.qbCustomers[companyId] || [];
      return customers.map((customer) => {
        // eslint-disable-next-line no-param-reassign
        customer.selectDisplay = this.customerDisplayName(customer);
        return customer;
      });
    },
    quickbooksSalesItems(companyId) {
      return this.qbSalesItems[companyId] || [];
    },
    quickbooksTaxCodes(companyId) /* : [TaxCode] */ {
      return this.qbTaxCodes[companyId] || [];
    },
    quickbooksSalesTaxCodes(companyId) /* : [TaxCode] */ {
      return _.chain(this.quickbooksTaxCodes(companyId))
        .filter(tc => _.get(tc, 'salesTaxRateList.taxRateDetail', []).length > 0)
        .value();
    },
    quickbooksPurchaseTaxCodes(companyId) /* : [TaxCode] */ {
      return _.chain(this.quickbooksTaxCodes(companyId))
        .filter(tc => _.get(tc, 'purchaseTaxRateList.taxRateDetail', []).length > 0)
        .value();
    },

    quickbooksSubscriptionSku(company) {
      const qb = this.quickbooksConnectFor(company);
      return _.get(qb, 'subscriptionSku', 'Unknown');
    },
    quickbooksSubscriptionSkuName(company) {
      const qb = this.quickbooksConnectFor(company);
      return _.get(qb, 'subscriptionSkuName', 'Unknown');
    },
    quickbooksCompanyName(company) {
      const qb = this.quickbooksConnectFor(company);
      return _.get(qb, 'companyName', null);
    },
    quickbooksCompanyLegalName(company) {
      const qb = this.quickbooksConnectFor(company);
      return _.get(qb, 'companyLegalName', null);
    },
    quickbooksCompanyAuthenticatedBy(company) {
      const qb = this.quickbooksConnectFor(company);
      return _.get(qb, 'authenticatedBy', null);
    },
    quickbooksCompanyAuthenticated(company) {
      const qb = this.quickbooksConnectFor(company);
      return _.get(qb, 'authenticated', null);
    },
    quickbooksCompanyAuthenticatedMoment(company) {
      const ts = this.quickbooksCompanyAuthenticated(company);
      return ts ? moment(ts, moment.ISO_8601) : null;
    },
    quickbooksCompanyCredentialExpires(company) {
      const qb = this.quickbooksConnectFor(company);
      return _.get(qb, 'credentialExpires', null);
    },
    quickbooksCompanyCredentialExpiresMoment(company) {
      const ts = this.quickbooksCompanyCredentialExpires(company);
      return ts ? moment(ts, moment.ISO_8601) : null;
    },
    quickbooksCompanyCollectsTax(company) {
      const qb = this.quickbooksConnectFor(company);
      return _.get(qb, 'configuredToCollectTax', false);
    },
    quickbooksSubscriptionSupportsBilling(company) {
      // We know Online Plus and Advanced work. And presumably any more expensive than those.
      const sku = this.quickbooksSubscriptionSku(company);
      const knownWorking = [
        'QuickbooksOnlinePlus',
        'QuickbooksOnlineAdvanced'
      ];
      const knownFail = [
        'QuickbooksOnlineSimpleStart',
        'QuickbooksOnlineEssentials'
      ];

      if (knownFail.includes(sku)) {
        return false;
      }
      return true;
    },
    defaultCompanyQuickbooksTaxCodes(company) {
      const salesCodeId = this.defaultCompanyQuickbooksInvoiceSalesTaxCode(company) || null;
      const purchaseCodeId = this.defaultCompanyQuickbooksBillPurchaseTaxCode(company) || null;

      let salesCode = { id: null };
      let purchaseCode = { id: null };

      if (salesCodeId) {
        const salesCodes = this.quickbooksSalesTaxCodes(company.id);
        salesCode = _.chain(salesCodes)
          .filter(tc => salesCodeId)
          .filter(tc => tc.id.toString() === salesCodeId.toString())
          .first()
          .value() || { id: null };
      }

      if (purchaseCodeId) {
        const purchaseCodes = this.quickbooksPurchaseTaxCodes(company.id);
        purchaseCode = _.chain(purchaseCodes)
          .filter(tc => purchaseCodeId)
          .filter(tc => tc.id.toString() === purchaseCodeId.toString())
          .first()
          .value() || { id: null };
      }

      return {
        sales: salesCode,
        purchase: purchaseCode,
      };
    },
    defaultCompanyQuickbooksInvoiceSalesTaxCode(company) {
      const qb = this.quickbooksConnectFor(company);
      return _.get(qb, 'exportDefaults.invoices.taxCode', null);
    },
    defaultCompanyQuickbooksBillPurchaseTaxCode(company) {
      const qb = this.quickbooksConnectFor(company);
      return _.get(qb, 'exportDefaults.bills.taxCode', null);
    },
    defaultCompanyQuickbooksInvoiceSalesItem(company) {
      const qb = this.quickbooksConnectFor(company);
      const id = _.get(qb, 'exportDefaults.invoices.salesItemId');
      if (id) {
        return _.find(this.quickbooksSalesItems(company.id), e => e.itemId === id);
      }

      return null;
    },
    defaultCompanyQuickbooksCustomerInvoiceTerms(company) {
      const qb = this.quickbooksConnectFor(company);
      const id = _.get(qb, 'exportDefaults.invoices.paymentTermsId');
      if (id) {
        return _.find(this.quickbooksPaymentTerms(company.id), e => e.id === id);
      }

      return null;
    },
    defaultCompanyQuickbooksVendorBillTerms(company) {
      const qb = this.quickbooksConnectFor(company);
      const id = _.get(qb, 'exportDefaults.bills.paymentTermsId');
      if (id) {
        return _.find(this.quickbooksPaymentTerms(company.id), e => e.id === id);
      }

      return null;
    },
    defaultCompanyQuickbooksTransportationFeeExpenseAccount(company) {
      const qb = this.quickbooksConnectFor(company);
      const id = _.get(qb, 'exportDefaults.bills.transportationExpenseAccountId');
      if (id) {
        return _.find(this.quickbooksAccounts(company.id), e => e.accountId === id);
      }

      return null;
    },
    defaultCompanyQuickbooksDispatchFeeIncomeAccount(company) {
      const qb = this.quickbooksConnectFor(company);
      const id = _.get(qb, 'exportDefaults.bills.dispatchFeeIncomeAccountId');
      if (id) {
        return _.find(this.quickbooksAccounts(company.id), e => e.accountId === id);
      }

      return null;
    },
    defaultCompanyQuickbooksTrailerLeaseFeeIncomeAccount(company) {
      const qb = this.quickbooksConnectFor(company);
      const id = _.get(qb, 'exportDefaults.bills.trailerLeaseFeeIncomeAccountId');
      if (id) {
        return _.find(this.quickbooksAccounts(company.id), e => e.accountId === id);
      }

      return null;
    },
    loadCompanyQuickbooksInfo(companyId) {
      this.refreshCompanyQuickbooksInfo(companyId);
      this.refreshQuickbooksSalesItems(companyId);
      this.refreshQuickbooksPaymentTerms(companyId);
      this.refreshQuickbooksAccounts(companyId);
      this.refreshQuickbooksVendors(companyId);
      this.refreshQuickbooksCustomers(companyId);
      this.refreshQuickbooksTaxCodes(companyId);
    },
    refreshCompanyQuickbooksInfo(companyId) {
      this.$store.dispatch('quickbooks/reloadCompanyInfo', companyId);
    },
    refreshQuickbooksSalesItems(companyId) {
      this.$store.dispatch('quickbooks/getSalesItems', companyId);
    },
    refreshQuickbooksPaymentTerms(companyId) {
      this.$store.dispatch('quickbooks/getTerms', companyId);
    },
    refreshQuickbooksAccounts(companyId) {
      this.$store.dispatch('quickbooks/getQBAccounts', companyId);
    },
    refreshQuickbooksVendors(companyId) {
      this.$store.dispatch('quickbooks/getVendors', companyId);
    },
    refreshQuickbooksCustomers(companyId) {
      this.$store.dispatch('quickbooks/getQBCustomers', companyId);
    },
    refreshQuickbooksTaxCodes(companyId) {
      this.$store.dispatch('quickbooks/getQBTaxCodes', companyId);
    },
    isCompanyQuickbooksInfoLoading(companyId) {
      return this.customersStatus[companyId] ||
             this.accountsStatus[companyId] ||
             this.vendorsStatus[companyId] ||
             this.salesTermsStatus[companyId] ||
             this.salesItemsStatus[companyId] ||
             this.taxCodesStatus[companyId] ||
             false;
    },

    quickbooksVendorDrivers(companyEmployees, vendors) {
      const vendorsByQbId = _.groupBy(vendors, v => v.qbCompanyId);

      const drivers = _.filter(companyEmployees, ee => ee.driverProfile);
      const driverVendors = _.filter(drivers, d => d.quickbooksVendorId);

      _.forEach(driverVendors, (dv) => {
        const vendorList = vendorsByQbId[dv.quickbooksVendorId];
        if (vendorList && vendorList.length > 0) {
          // eslint-disable-next-line no-param-reassign, no-underscore-dangle
          dv.__vendor = vendorList[0];
        }
      });

      return driverVendors;
    },
  },
  computed: {
    ...mapGetters('profiles', {
      isLtnAdmin: 'isLtnAdmin',
    }),
    ...mapGetters('quickbooks', {
      qbCustomers: 'customers',
      customersStatus: 'customersStatus',
      qbAccounts: 'accounts',
      accountsStatus: 'accountsStatus',
      qbVendors: 'vendors',
      vendorsStatus: 'vendorsStatus',
      qbSalesTerms: 'salesTerms',
      salesTermsStatus: 'salesTermsStatus',
      qbSalesItems: 'salesItems',
      salesItemsStatus: 'salesItemsStatus',
      qbTaxCodes: 'taxCodes',
      taxCodesStatus: 'taxCodesStatus',
    }),
    quickbooksSubscriptionRequiredForBilling() {
      return 'Exporting transactions requires the Billing features in QBO, at least an Online Plus subscription';
    },
  },
};



// WEBPACK FOOTER //
// ./src/mixins/quickbooksMixin.js